import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invitationService } from '../services/invitationService';
import {
    Container,
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';

const InvitationResponse = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const fetchInvitation = async () => {
            try {
                const response = await invitationService.getInvitationByToken(token);
                setInvitation(response.data);
            } catch (error) {
                setError('Invalid or expired invitation');
            } finally {
                setLoading(false);
            }
        };

        fetchInvitation();
    }, [token]);

    const handleTimeSlotToggle = (timeSlotId) => {
        setSelectedTimeSlots(prev =>
            prev.includes(timeSlotId)
                ? prev.filter(id => id !== timeSlotId)
                : [...prev, timeSlotId]
        );
    };

    const handleAccept = async () => {
        if (!user) {
            enqueueSnackbar('Please log in to accept the invitation', { variant: 'warning' });
            return;
        }

        try {
            await invitationService.acceptInvitation(token, user.id);
            enqueueSnackbar('Invitation accepted successfully', { variant: 'success' });
            navigate(`/events/${invitation.event.id}`);
        } catch (error) {
            enqueueSnackbar('Failed to accept invitation', { variant: 'error' });
        }
    };

    const handleDecline = async () => {
        try {
            await invitationService.declineInvitation(token);
            enqueueSnackbar('Invitation declined', { variant: 'info' });
            navigate('/');
        } catch (error) {
            enqueueSnackbar('Failed to decline invitation', { variant: 'error' });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Event Invitation
                </Typography>

                <Typography variant="h6" gutterBottom>
                    {invitation.event.title}
                </Typography>

                <Typography paragraph>
                    {invitation.event.description}
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Event Details
                                </Typography>
                                <Typography>
                                    <strong>Location:</strong> {invitation.event.location || 'Not specified'}
                                </Typography>
                                <Typography>
                                    <strong>Start Time:</strong> {new Date(invitation.event.startTime).toLocaleString()}
                                </Typography>
                                <Typography>
                                    <strong>End Time:</strong> {new Date(invitation.event.endTime).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Available Time Slots
                                </Typography>
                                {invitation.event.timeSlots.map((timeSlot) => (
                                    <FormControlLabel
                                        key={timeSlot.id}
                                        control={
                                            <Checkbox
                                                checked={selectedTimeSlots.includes(timeSlot.id)}
                                                onChange={() => handleTimeSlotToggle(timeSlot.id)}
                                            />
                                        }
                                        label={`${new Date(timeSlot.proposedStartTime).toLocaleString()} - ${new Date(timeSlot.proposedEndTime).toLocaleString()}`}
                                    />
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDecline}
                    >
                        Decline
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAccept}
                        disabled={!user}
                    >
                        Accept
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default InvitationResponse; 